import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@/components/ThemeProvider";
import { ChevronsLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import logoDark from "@/assets/shattara-ai-dark.png";
import logoLight from "@/assets/shattara-ai-light.png";

interface PublicSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PublicSidebar: React.FC<PublicSidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const logo = theme === "light" ? logoLight : logoDark;

  return (
    <aside
      className={`fixed left-0 top-0 z-50 flex h-full w-64 flex-col border-r border-border bg-background transition-transform duration-300 lg:z-30 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* Top: Logo + Collapse */}
      <div className="flex items-center justify-between px-4 py-3">
        <a href="/">
          <img src={logo} className="h-10 w-auto" alt="Shattara AI" />
        </a>
        <button
          onClick={onClose}
          className="flex h-8 w-8 items-center justify-center rounded-xl text-muted-foreground hover:bg-muted transition"
          aria-label="Collapse sidebar"
        >
          <ChevronsLeft className="h-5 w-5" />
        </button>
      </div>

      {/* Center: Welcome message */}
      <div className="flex-1 px-5 pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Welcome to Shattara AI
        </h3>
        <p className="text-sm font-medium text-foreground/80 mb-3">
          An AI tutor made for you.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Shattara AI turns learning materials into concise notes, interactive tutoring, and personalized tests.
        </p>
        <p className="text-sm text-muted-foreground leading-relaxed mt-3">
          We are continuously improving the platform and welcome your feedback.
        </p>
      </div>

      {/* Bottom: Sign In CTA */}
      <div className="px-4 pb-4">
        <Button
          onClick={() => navigate("/auth/signin")}
          className="w-full h-11 text-base font-medium"
        >
          Sign In
        </Button>
      </div>
    </aside>
  );
};
